import * as React from 'react';
import { Cell, flexRender, Row } from '@tanstack/react-table';
import type { ExtendedViewership } from './types';

function toKebabCase(input: string): string {
  return input
    .replace(/([a-z])([A-Z])/g, '$1-$2') // Insert a hyphen between lowercase and uppercase letters
    .toLowerCase(); // Convert the entire string to lowercase
}

const getCellClassName = (cell: Cell<ExtendedViewership, unknown>) => {
  const classNames = ['viewership-cell'];
  if (cell.column.id === 'label') {
    classNames.push('label-cell');
  }

  // last row is only clickable for past365Impressions
  const isClickable = cell.row.original.isPinnedBottom
    ? cell.column.id === 'past365Impressions'
    : [
        'premiereImpressions',
        'past30Impressions',
        'past60Impressions',
        'past90Impressions',
        'past365Impressions',
      ].includes(cell.column.id);

  if (isClickable) {
    classNames.push('clickable-cell');
  }

  classNames.push(toKebabCase(cell.column.id));

  return classNames.join(' ');
};

export function CellRenderer({
  cell,
  subRow,
  onCellClick,
}: {
  readonly cell: Cell<ExtendedViewership, unknown>;
  readonly subRow: Row<ExtendedViewership>;
  readonly onCellClick: (
    event: React.MouseEvent<HTMLTableCellElement>,
    cell: Cell<ExtendedViewership, unknown>,
    row: Row<ExtendedViewership>,
  ) => void;
}) {
  // Get CSS classes and check if cell is clickable
  const cellClassName = getCellClassName(cell);
  const isClickableCell = cellClassName.includes('clickable-cell');

  const handleClick = (event: React.MouseEvent<HTMLTableCellElement>) => {
    onCellClick(event, cell, subRow);
  };

  // Handle keyboard events for accessibility
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableCellElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      // Create a synthetic mouse event for the handler
      const syntheticEvent = (event as unknown) as React.MouseEvent<HTMLTableCellElement>;

      handleClick(syntheticEvent);
    }
  };

  // Only add interactive attributes if the cell is clickable
  const interactiveProps = isClickableCell
    ? {
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        tabIndex: 0,
        role: 'button',
        'aria-label': `View details for ${cell.column.id}`,
      }
    : { role: 'cell' };

  return (
    <td
      key={cell.id}
      data-cell-id={`${cell.id}-subrow-${subRow.id}`}
      className={cellClassName}
      {...interactiveProps}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  );
}
